import {
  normalizeConfiguredAgents,
  normalizeConfiguredTables,
} from "../agentConfig/agentConfigDraftModel";
import {
  SELF_CONFIG_SCOPE_LIST_FIELDS,
  SELF_CONFIG_SCOPE_MODE_LABELS,
} from "../agentConfig/agentSelfConfigScopeFields";
import { parseMaybeJson } from "./conversationUtils";

function coerceJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("[")) return value;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : value;
    } catch {
      return value;
    }
  }
  return value;
}

function scopeArgsWithCoercedLists(parsedArgs) {
  if (!parsedArgs || typeof parsedArgs !== "object") return parsedArgs;
  const next = { ...parsedArgs };
  for (const key of SELF_CONFIG_SCOPE_LIST_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = coerceJsonArray(next[key]);
    }
  }
  return next;
}

function normalizeWorkflowConfigs(values) {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const workflowId = String(value?.workflowId || value?.id || "").trim();
    if (!workflowId || deduped.has(workflowId)) return;
    const workflowName = String(
      value?.workflowName || value?.name || workflowId
    ).trim();
    deduped.set(workflowId, {
      workflowId,
      workflowName: workflowName || workflowId,
    });
  });
  return Array.from(deduped.values());
}

function normalizeDocRefs(values) {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const docId =
      typeof value === "string"
        ? value.trim()
        : String(value?.docId || value?.id || "").trim();
    if (!docId || deduped.has(docId)) return;
    const title =
      typeof value === "string"
        ? docId
        : String(value?.title || value?.name || docId).trim() || docId;
    deduped.set(docId, { docId, title });
  });
  return Array.from(deduped.values());
}

function normalizeMcpRefs(values) {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const serverId = String(value?.serverId || "").trim();
    if (!serverId || deduped.has(serverId)) return;
    deduped.set(serverId, {
      serverId,
      label: serverId,
    });
  });
  return Array.from(deduped.values());
}

function diffKeyedLists(currentItems, proposedItems, keyFn, labelFn) {
  const currentByKey = new Map(
    (currentItems || []).map((item) => [keyFn(item), labelFn(item)])
  );
  const proposedByKey = new Map(
    (proposedItems || []).map((item) => [keyFn(item), labelFn(item)])
  );
  const adding = [];
  const removing = [];
  for (const [key, label] of proposedByKey) {
    if (!currentByKey.has(key)) adding.push(label);
  }
  for (const [key, label] of currentByKey) {
    if (!proposedByKey.has(key)) removing.push(label);
  }
  if (adding.length === 0 && removing.length === 0) {
    const proposedKeys = [...proposedByKey.keys()].sort().join("\0");
    const currentKeys = [...currentByKey.keys()].sort().join("\0");
    if (proposedKeys !== currentKeys) {
      return {
        adding: [...proposedByKey.values()],
        removing: [],
        replaced: true,
      };
    }
    return null;
  }
  return { adding, removing, replaced: false };
}

function diffStringList(currentValues, proposedValues) {
  const current = normalizeStringList(currentValues);
  const proposed = normalizeStringList(proposedValues);
  if (proposed.length === 0) return null;
  const currentSet = new Set(current);
  const proposedSet = new Set(proposed);
  const adding = proposed.filter((v) => !currentSet.has(v));
  const removing = current.filter((v) => !proposedSet.has(v));
  if (adding.length === 0 && removing.length === 0) return null;
  return { adding, removing };
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
    .filter((v) => v.length > 0);
}

function formatScopeMode(value) {
  if (value === null || value === undefined || value === "") return "default";
  return String(value).replace(/_/g, " ").toLowerCase();
}

/**
 * Build human-readable rows for a scope-change approval card.
 * Each row: { sectionLabel, adding[], removing[], setting?, from?, to? }
 */
export function buildScopeChangeRows(parsedArgs, currentScope = {}) {
  const normalizedArgs = scopeArgsWithCoercedLists(parsedArgs);
  if (!normalizedArgs || typeof normalizedArgs !== "object") return [];

  const rows = [];

  if (Object.prototype.hasOwnProperty.call(normalizedArgs, "enabledTools")) {
    const diff = diffStringList(
      currentScope.enabledTools,
      normalizedArgs.enabledTools
    );
    if (diff) {
      rows.push({ sectionLabel: "Tools", ...diff });
    } else if (normalizeStringList(normalizedArgs.enabledTools).length > 0) {
      rows.push({
        sectionLabel: "Tools",
        setting: normalizeStringList(normalizedArgs.enabledTools),
      });
    }
  }

  if (Object.prototype.hasOwnProperty.call(normalizedArgs, "configuredTables")) {
    const current = normalizeConfiguredTables(currentScope.configuredTables);
    const proposed = normalizeConfiguredTables(normalizedArgs.configuredTables);
    const diff = diffKeyedLists(
      current,
      proposed,
      (item) => item.tableId,
      (item) => item.tableName || item.tableId
    );
    if (diff) {
      rows.push({ sectionLabel: "Tables", ...diff });
    } else if (proposed.length > 0) {
      rows.push({
        sectionLabel: "Tables",
        setting: proposed.map((t) => t.tableName || t.tableId),
      });
    }
  }

  if (Object.prototype.hasOwnProperty.call(normalizedArgs, "runWorkflowConfigs")) {
    const current = normalizeWorkflowConfigs(currentScope.runWorkflowConfigs);
    const proposed = normalizeWorkflowConfigs(normalizedArgs.runWorkflowConfigs);
    const diff = diffKeyedLists(
      current,
      proposed,
      (item) => item.workflowId,
      (item) => item.workflowName || item.workflowId
    );
    if (diff) {
      rows.push({ sectionLabel: "Workflows", ...diff });
    } else if (proposed.length > 0) {
      rows.push({
        sectionLabel: "Workflows",
        setting: proposed.map((w) => w.workflowName || w.workflowId),
      });
    }
  }

  if (Object.prototype.hasOwnProperty.call(normalizedArgs, "configuredAgents")) {
    const current = normalizeConfiguredAgents(currentScope.configuredAgents);
    const proposed = normalizeConfiguredAgents(normalizedArgs.configuredAgents);
    const diff = diffKeyedLists(
      current,
      proposed,
      (item) => item.agentId,
      (item) => item.name || item.agentId
    );
    if (diff) {
      rows.push({ sectionLabel: "Agents", ...diff });
    } else if (proposed.length > 0) {
      rows.push({
        sectionLabel: "Agents",
        setting: proposed.map((a) => a.name || a.agentId),
      });
    }
  }

  if (Object.prototype.hasOwnProperty.call(normalizedArgs, "configuredDocs")) {
    const current = normalizeDocRefs(currentScope.configuredDocs);
    const proposed = normalizeDocRefs(normalizedArgs.configuredDocs);
    const diff = diffKeyedLists(
      current,
      proposed,
      (item) => item.docId,
      (item) => item.title || item.docId
    );
    if (diff) {
      rows.push({ sectionLabel: "Documents", ...diff });
    } else if (proposed.length > 0) {
      rows.push({
        sectionLabel: "Documents",
        setting: proposed.map((d) => d.title || d.docId),
      });
    }
  }

  if (Object.prototype.hasOwnProperty.call(normalizedArgs, "mcpServerRefs")) {
    const current = normalizeMcpRefs(currentScope.mcpServerRefs);
    const proposed = normalizeMcpRefs(normalizedArgs.mcpServerRefs);
    const diff = diffKeyedLists(
      current,
      proposed,
      (item) => item.serverId,
      (item) => item.label
    );
    if (diff) {
      rows.push({ sectionLabel: "MCP servers", ...diff });
    } else if (proposed.length > 0) {
      rows.push({
        sectionLabel: "MCP servers",
        setting: proposed.map((s) => s.label),
      });
    }
  }

  for (const [field, sectionLabel] of Object.entries(
    SELF_CONFIG_SCOPE_MODE_LABELS
  )) {
    if (!Object.prototype.hasOwnProperty.call(normalizedArgs, field)) continue;
    const next = normalizedArgs[field];
    const prev = currentScope[field];
    if (prev !== undefined && String(prev) === String(next)) continue;
    rows.push({
      sectionLabel,
      from: formatScopeMode(prev),
      to: formatScopeMode(next),
    });
  }

  return rows;
}

/**
 * Resolve baseline scope for an agent from the latest get_self_config tool result
 * in the transcript (what the agent read before proposing a change).
 */
export function findSelfConfigScopeBaseline(messages, agentId) {
  if (!Array.isArray(messages) || !agentId) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role !== "assistant" || !Array.isArray(msg.parts)) continue;
    const msgAgentId = msg.agentId || msg.senderAgentId;
    if (msgAgentId && msgAgentId !== agentId) continue;
    for (let p = msg.parts.length - 1; p >= 0; p -= 1) {
      const part = msg.parts[p];
      if (part?.type !== "tool" || part?.toolName !== "get_self_config") {
        continue;
      }
      const raw =
        part.output ??
        part.result ??
        (part.state === "output-available" ? part.output : null);
      const parsed = parseMaybeJson(raw);
      if (parsed && typeof parsed === "object") {
        return scopeFromSelfConfig(parsed);
      }
    }
  }
  return null;
}

function scopeFromSelfConfig(config) {
  return {
    enabledTools: config.enabledTools || [],
    configuredTables: config.configuredTables || [],
    configuredAgents: config.configuredAgents || [],
    runWorkflowConfigs: config.runWorkflowConfigs || [],
    configuredDocs: config.configuredDocs || [],
    mcpServerRefs: config.mcpServerRefs || [],
    tableScopeMode: config.tableScopeMode,
    workflowScopeMode: config.workflowScopeMode,
    agentScopeMode: config.agentScopeMode,
    fileScopeMode: config.fileScopeMode,
    artifactScopeMode: config.artifactScopeMode,
    docScopeMode: config.docScopeMode,
  };
}

export function resolveScopeBaseline({
  agentId,
  currentAgentScopeById,
  messages,
}) {
  const fromSelfConfig = findSelfConfigScopeBaseline(messages, agentId);
  if (fromSelfConfig) return fromSelfConfig;
  if (currentAgentScopeById && agentId && currentAgentScopeById[agentId]) {
    return currentAgentScopeById[agentId];
  }
  return {};
}
